<template>
  <div class="min-h-screen bg-[#FAFAF8] px-6 py-8">
    <div class="max-w-6xl mx-auto space-y-8">
      <!-- En-tête -->
      <div
        class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 class="text-3xl font-extrabold text-[#0B2447]">
            Suivi de la Maintenance
          </h1>
          <p class="text-gray-500 text-sm mt-1">
            Tableau de bord des interventions techniques récurrentes mensuelles
          </p>
        </div>

        <div
          class="flex items-center gap-2 bg-white rounded-xl shadow-sm px-4 py-2 border border-gray-100"
        >
          <button
            @click="changeMonth(-1)"
            class="text-gray-400 hover:text-orange-600 transition text-lg font-bold"
          >
            ←
          </button>
          <span
            class="font-bold text-[#0B2447] min-w-32 text-center uppercase text-sm tracking-wider"
            >{{ formattedMonth }}</span
          >
          <button
            @click="changeMonth(1)"
            class="text-gray-400 hover:text-orange-600 transition text-lg font-bold"
          >
            →
          </button>
        </div>
      </div>

      <!-- Chargement -->
      <div v-if="isLoading" class="text-center py-12 text-gray-400">
        Chargement...
      </div>

      <!-- Liste vide -->
      <div
        v-else-if="logs.length === 0"
        class="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100 text-gray-400"
      >
        Aucun projet actif à suivre ce mois-ci.
      </div>

      <!-- Grille -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div
          v-for="log in logs"
          :key="log.id"
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 space-y-6 flex flex-col justify-between"
        >
          <!-- Haut de carte -->
          <div>
            <div class="flex justify-between items-start gap-4">
              <h3 class="font-extrabold text-lg text-[#0B2447] leading-tight">
                {{ log.project_title }}
              </h3>
              <span
                class="text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-100 text-orange-700"
                >{{ log.project_type || 'Général' }}</span
              >
            </div>
            <!-- Barre de progression -->
            <div class="h-1.5 bg-gray-100 rounded-full mt-3 overflow-hidden">
              <div
                class="h-full bg-orange-500 transition-all duration-500"
                :style="{ width: calculateProgress(log) + '%' }"
              ></div>
            </div>
            <div class="flex justify-between mt-1">
              <p class="text-[10px] text-gray-400 uppercase tracking-wider">
                Interventions
              </p>
              <span class="text-[10px] font-bold text-orange-600"
                >{{ calculateProgress(log) }}%</span
              >
            </div>
          </div>

          <!-- Milieu : Checklist -->
          <div class="space-y-3.5">
            <label
              v-for="(task, key) in tasksConfig"
              :key="key"
              class="flex items-center gap-3 cursor-pointer group"
            >
              <input
                type="checkbox"
                v-model="log[key]"
                @change="updateLog(log)"
                class="h-5 w-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer accent-orange-600"
              />
              <span
                :class="[
                  'text-sm font-medium transition',
                  log[key]
                    ? 'text-gray-400 line-through'
                    : 'text-gray-700 group-hover:text-orange-600'
                ]"
              >
                {{ task }}
              </span>
            </label>
          </div>

          <!-- Bas de carte : Notes -->
          <div class="pt-4 border-t border-gray-100 space-y-2">
            <div class="flex justify-between">
              <span
                class="text-xs font-bold text-[#0B2447] uppercase tracking-wider"
                >Notes :</span
              >
              <span
                v-if="updatingIds.has(log.id)"
                class="text-[10px] text-orange-500 animate-pulse"
                >Sauvegarde...</span
              >
            </div>
            <textarea
              v-model="log.notes"
              @change="updateLog(log)"
              rows="2"
              class="w-full p-2.5 text-sm border-2 border-gray-100 rounded-lg outline-none focus:border-orange-500 transition resize-none bg-gray-50"
              placeholder="Rédigez un court compte-rendu..."
            ></textarea>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/apiClient'

export default {
  name: 'MaintenanceView',
  data() {
    return {
      logs: [],
      currentDate: new Date(),
      isLoading: false,
      updatingIds: new Set(),
      tasksConfig: {
        backup_done: 'Sauvegardes (Backups fichiers & base de données)',
        updates_done: 'Mises à jour (Core, Plugins & Dépendances)',
        security_done: 'Sécurité (Audit, Pare-feu, Analyse des logs)',
        performance_done: 'Optimisation (Vitesse, Nettoyage Cache & DB)',
        ssl_domain_done: 'SSL & Domaine (Vérification validité)'
      }
    }
  },
  computed: {
    monthQueryString() {
      const year = this.currentDate.getFullYear()
      const month = String(this.currentDate.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    },
    formattedMonth() {
      return this.currentDate.toLocaleDateString('fr-FR', {
        month: 'long',
        year: 'numeric'
      })
    }
  },
  methods: {
    calculateProgress(log) {
      const keys = Object.keys(this.tasksConfig)
      const done = keys.filter((k) => log[k] === true).length
      return Math.round((done / keys.length) * 100)
    },
    async fetchMaintenanceLogs() {
      this.isLoading = true
      try {
        const res = await apiClient.get(
          `/api/v1/maintenance_logs?month=${this.monthQueryString}`
        )
        this.logs = res.data
      } catch (err) {
        console.error(err)
      } finally {
        this.isLoading = false
      }
    },
    async updateLog(log) {
      this.updatingIds.add(log.id)
      try {
        await apiClient.put(`/api/v1/maintenance_logs/${log.id}`, {
          maintenance_log: log
        })
      } catch (err) {
        console.error('Erreur sauvegarde', err)
      } finally {
        this.updatingIds.delete(log.id)
      }
    },
    changeMonth(direction) {
      this.currentDate.setMonth(this.currentDate.getMonth() + direction)
      this.fetchMaintenanceLogs()
    }
  },
  mounted() {
    this.fetchMaintenanceLogs()
  }
}
</script>
